import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCopy } from './copy/copyMode';
import { LayoutGrid, MessageSquare, FolderOpen, Bot, Plus, Upload, ChevronDown, Activity, BarChart3, Kanban, AlertCircle, TrendingUp, Users, DollarSign, Zap, Target, Map, MessageCircle, HeartPulse, Ticket, Gauge, Plug } from 'lucide-react';
type Persona = 'Leadership' | 'Sales' | 'Marketing' | 'Product' | 'Engineering' | 'Customer Success' | 'Operations';
interface AppCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  connections: number;
  uiPreview: React.ReactNode;
}
const personaData: Record<Persona, AppCard[]> = {
  Sales: [{
    title: 'GTM Pipeline Tracker',
    description: 'Kanban and funnel pipeline with deal forecasting, signal alerts, and account drill-downs.',
    icon: <Kanban size={16} className="text-neutral-400" data-id="element-2014" />,
    connections: 4,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-2015">
          {/* Top stats */}
          <div className="flex gap-4 mb-2" data-id="element-2016">
            <div className="flex flex-col" data-id="element-2017">
              <span className="text-[10px] text-neutral-500" data-id="element-2018">
                Total Pipeline
              </span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-2019">$2.4M</span>
            </div>
            <div className="flex flex-col" data-id="element-2020">
              <span className="text-[10px] text-neutral-500" data-id="element-2021">Active Deals</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-2022">18</span>
            </div>
            <div className="flex flex-col" data-id="element-2023">
              <span className="text-[10px] text-neutral-500" data-id="element-2024">Win Rate</span>
              <span className="text-sm font-bold text-green-600" data-id="element-2025">32%</span>
            </div>
          </div>
          {/* Funnel bars */}
          <div className="flex flex-col gap-2 flex-1 justify-end" data-id="element-2026">
            <div className="flex items-center gap-2" data-id="element-2027">
              <span className="text-[9px] text-neutral-500 w-12" data-id="element-2028">
                Discovery
              </span>
              <div className="h-2 bg-blue-500 rounded-full w-full" data-id="element-2029" />
            </div>
            <div className="flex items-center gap-2" data-id="element-2030">
              <span className="text-[9px] text-neutral-500 w-12" data-id="element-2031">Demo</span>
              <div className="h-2 bg-indigo-500 rounded-full w-[75%]" data-id="element-2032" />
            </div>
            <div className="flex items-center gap-2" data-id="element-2033">
              <span className="text-[9px] text-neutral-500 w-12" data-id="element-2034">Proposal</span>
              <div className="h-2 bg-purple-500 rounded-full w-[45%]" data-id="element-2035" />
            </div>
            <div className="flex items-center gap-2" data-id="element-2036">
              <span className="text-[9px] text-neutral-500 w-12" data-id="element-2037">
                Negotiation
              </span>
              <div className="h-2 bg-pink-500 rounded-full w-[20%]" data-id="element-2038" />
            </div>
          </div>
        </div>
  }, {
    title: 'Deal War Room',
    description: 'Live deal command center with signals, stakeholders, and next-step plays.',
    icon: <Activity size={16} className="text-neutral-400" data-id="element-2039" />,
    connections: 3,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-2040">
          {/* Table header */}
          <div className="grid grid-cols-4 gap-2 border-b border-neutral-200 pb-2" data-id="element-2041">
            <span className="text-[9px] text-neutral-500 col-span-2" data-id="element-2042">
              ACCOUNT
            </span>
            <span className="text-[9px] text-neutral-500" data-id="element-2043">VALUE</span>
            <span className="text-[9px] text-neutral-500" data-id="element-2044">STATUS</span>
          </div>
          {/* Table rows */}
          <div className="flex flex-col gap-2" data-id="element-2045">
            <div className="grid grid-cols-4 gap-2 items-center" data-id="element-2046">
              <div className="flex items-center gap-1.5 col-span-2" data-id="element-2047">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-2048" />
                <span className="text-[10px] text-[#333333] font-medium truncate" data-id="element-2049">
                  Acme Corp
                </span>
              </div>
              <span className="text-[10px] text-neutral-600" data-id="element-2050">$120k</span>
              <span className="text-[9px] text-[#433832] bg-[#FAF8F4] px-1.5 py-0.5 rounded border border-[#EAE4D9]" data-id="element-2051">
                Review
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2 items-center" data-id="element-2052">
              <div className="flex items-center gap-1.5 col-span-2" data-id="element-2053">
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" data-id="element-2054" />
                <span className="text-[10px] text-[#333333] font-medium truncate" data-id="element-2055">
                  Globex
                </span>
              </div>
              <span className="text-[10px] text-neutral-600" data-id="element-2056">$85k</span>
              <span className="text-[9px] text-[#433832] bg-[#FAF8F4] px-1.5 py-0.5 rounded border border-[#EAE4D9]" data-id="element-2057">
                Proposal
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2 items-center" data-id="element-2058">
              <div className="flex items-center gap-1.5 col-span-2" data-id="element-2059">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-2060" />
                <span className="text-[10px] text-[#333333] font-medium truncate" data-id="element-2061">
                  Initech
                </span>
              </div>
              <span className="text-[10px] text-neutral-600" data-id="element-2062">$250k</span>
              <span className="text-[9px] text-[#433832] bg-[#FAF8F4] px-1.5 py-0.5 rounded border border-[#EAE4D9]" data-id="element-2063">
                Stalled
              </span>
            </div>
          </div>
        </div>
  }],
  Marketing: [{
    title: 'Campaign ROI Tracker',
    description: 'Real-time view of ad spend vs CAC across Meta, Google, and LinkedIn.',
    icon: <TrendingUp size={16} className="text-neutral-400" data-id="element-2064" />,
    connections: 5,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-2065">
          <div className="flex justify-between items-end mb-2" data-id="element-2066">
            <div className="flex flex-col" data-id="element-2067">
              <span className="text-[10px] text-neutral-500" data-id="element-2068">Total Spend</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-2069">$45.2k</span>
            </div>
            <div className="flex flex-col text-right" data-id="element-2070">
              <span className="text-[10px] text-neutral-500" data-id="element-2071">Blended CAC</span>
              <span className="text-sm font-bold text-green-600" data-id="element-2072">$124</span>
            </div>
          </div>
          {/* Bar chart mockup */}
          <div className="flex items-end gap-2 h-16 mt-auto border-b border-neutral-200 pb-1" data-id="element-2073">
            {[40, 70, 45, 90, 65, 85, 100].map((h, i) => <div key={i} className="flex-1 bg-pink-400 rounded-t-sm" style={{
          height: `${h}%`
        }} data-id="element-2074" />)}
          </div>
        </div>
  }, {
    title: 'Content Engine',
    description: 'Pipeline of drafts, published assets, and engagement metrics from social.',
    icon: <BarChart3 size={16} className="text-neutral-400" data-id="element-2075" />,
    connections: 3,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-2076">
          <div className="grid grid-cols-2 gap-2" data-id="element-2077">
            <div className="bg-white rounded p-2 border border-neutral-200 shadow-sm" data-id="element-2078">
              <span className="text-[9px] text-neutral-500 block mb-1" data-id="element-2079">
                Drafts
              </span>
              <span className="text-xs font-bold text-[#333333]" data-id="element-2080">12</span>
            </div>
            <div className="bg-white rounded p-2 border border-neutral-200 shadow-sm" data-id="element-2081">
              <span className="text-[9px] text-neutral-500 block mb-1" data-id="element-2082">
                Published
              </span>
              <span className="text-xs font-bold text-[#333333]" data-id="element-2083">48</span>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 mt-2" data-id="element-2084">
            <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden" data-id="element-2085">
              <div className="h-full bg-orange-400 w-[60%]" data-id="element-2086" />
            </div>
            <span className="text-[8px] text-neutral-500 text-right" data-id="element-2087">
              60% to monthly goal
            </span>
          </div>
        </div>
  }],
  Engineering: [{
    title: 'Incident Command',
    description: 'Aggregated alerts from Sentry and Datadog with auto-triage status.',
    icon: <AlertCircle size={16} className="text-neutral-400" data-id="element-2088" />,
    connections: 4,
    uiPreview: <div className="flex flex-col gap-2 h-full" data-id="element-2089">
          <div className="bg-red-50 border border-red-100 rounded p-2 flex gap-2 items-start" data-id="element-2090">
            <AlertCircle size={12} className="text-red-500 mt-0.5 shrink-0" data-id="element-2091" />
            <div data-id="element-2092">
              <span className="text-[10px] font-medium text-red-700 block" data-id="element-2093">
                API Latency Spike
              </span>
              <span className="text-[8px] text-red-500" data-id="element-2094">
                US-East-1 · 2m ago
              </span>
            </div>
          </div>
          <div className="bg-white border border-neutral-200 shadow-sm rounded p-2 flex gap-2 items-start" data-id="element-2095">
            <Zap size={12} className="text-yellow-500 mt-0.5 shrink-0" data-id="element-2096" />
            <div data-id="element-2097">
              <span className="text-[10px] font-medium text-[#333333] block" data-id="element-2098">
                DB Connection Pool
              </span>
              <span className="text-[8px] text-neutral-500" data-id="element-2099">
                Warning · 15m ago
              </span>
            </div>
          </div>
        </div>
  }, {
    title: 'Sprint Velocity',
    description: 'Linear burn-down charts and PR review bottlenecks from GitHub.',
    icon: <Zap size={16} className="text-neutral-400" data-id="element-2100" />,
    connections: 2,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-2101">
          <div className="flex justify-between items-center mb-1" data-id="element-2102">
            <span className="text-[10px] text-neutral-500" data-id="element-2103">Sprint 42</span>
            <span className="text-[9px] text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded" data-id="element-2104">
              On Track
            </span>
          </div>
          {/* Line chart mockup */}
          <div className="relative h-16 mt-auto border-l border-b border-neutral-200" data-id="element-2105">
            <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-2106">
              <path d="M0,10 L20,30 L40,25 L60,60 L80,55 L100,90" fill="none" stroke="#22c55e" strokeWidth="2" vectorEffect="non-scaling-stroke" data-id="element-2107" />
              <path d="M0,0 L100,100" fill="none" stroke="#e5e5e5" strokeWidth="1" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" data-id="element-2108" />
            </svg>
          </div>
        </div>
  }],
  Operations: [{
    title: 'Vendor Spend',
    description: 'Consolidated SaaS and vendor spend from Xero, Ramp, and Stripe.',
    icon: <DollarSign size={16} className="text-neutral-400" data-id="element-2109" />,
    connections: 3,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-2110">
          <div className="flex items-baseline gap-2 mb-2" data-id="element-2111">
            <span className="text-lg font-bold text-[#333333]" data-id="element-2112">$142.5k</span>
            <span className="text-[9px] text-red-500" data-id="element-2113">+12% MoM</span>
          </div>
          <div className="flex flex-col gap-2" data-id="element-2114">
            <div className="flex justify-between items-center" data-id="element-2115">
              <span className="text-[10px] text-neutral-500" data-id="element-2116">AWS</span>
              <span className="text-[10px] text-[#333333] font-medium" data-id="element-2117">
                $45k
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-2118">
              <span className="text-[10px] text-neutral-500" data-id="element-2119">Salesforce</span>
              <span className="text-[10px] text-[#333333] font-medium" data-id="element-2120">
                $22k
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-2121">
              <span className="text-[10px] text-neutral-500" data-id="element-2122">Datadog</span>
              <span className="text-[10px] text-[#333333] font-medium" data-id="element-2123">
                $18k
              </span>
            </div>
          </div>
        </div>
  }, {
    title: 'Headcount Plan',
    description: 'Open roles vs budget, pulling from Greenhouse and Workday.',
    icon: <Users size={16} className="text-neutral-400" data-id="element-2124" />,
    connections: 2,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-2125">
          <div className="grid grid-cols-2 gap-2 mb-2" data-id="element-2126">
            <div className="flex flex-col" data-id="element-2127">
              <span className="text-[9px] text-neutral-500" data-id="element-2128">Open Roles</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-2129">14</span>
            </div>
            <div className="flex flex-col" data-id="element-2130">
              <span className="text-[9px] text-neutral-500" data-id="element-2131">Offers Out</span>
              <span className="text-sm font-bold text-[#433832]" data-id="element-2132">3</span>
            </div>
          </div>
          <div className="flex flex-col gap-1.5" data-id="element-2133">
            <span className="text-[9px] text-neutral-500" data-id="element-2134">Engineering (8)</span>
            <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden" data-id="element-2135">
              <div className="h-full bg-indigo-500 w-[70%]" data-id="element-2136" />
            </div>
            <span className="text-[9px] text-neutral-500 mt-1" data-id="element-2137">Sales (4)</span>
            <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden" data-id="element-2138">
              <div className="h-full bg-indigo-500 w-[30%]" data-id="element-2139" />
            </div>
          </div>
        </div>
  }],
  Leadership: [{
    title: 'Business Pulse',
    description: 'Revenue, pipeline, and runway in one glance – pulled live from Stripe and your CRM.',
    icon: <Gauge size={16} className="text-neutral-400" data-id="element-2140" />,
    connections: 4,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-2141">
          <div className="grid grid-cols-3 gap-2 mb-1" data-id="element-2142">
            <div className="flex flex-col" data-id="element-2143">
              <span className="text-[9px] text-neutral-500" data-id="element-2144">MRR</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-2145">$182k</span>
              <span className="text-[8px] text-green-600" data-id="element-2146">+8% MoM</span>
            </div>
            <div className="flex flex-col" data-id="element-2147">
              <span className="text-[9px] text-neutral-500" data-id="element-2148">Pipeline</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-2149">$2.4M</span>
              <span className="text-[8px] text-neutral-400" data-id="element-2150">42 deals</span>
            </div>
            <div className="flex flex-col" data-id="element-2151">
              <span className="text-[9px] text-neutral-500" data-id="element-2152">Runway</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-2153">18mo</span>
              <span className="text-[8px] text-neutral-400" data-id="element-2154">$3.1M cash</span>
            </div>
          </div>
          <div className="flex items-end gap-1.5 h-12 mt-auto border-b border-neutral-200 pb-1" data-id="element-2155">
            {[50, 62, 58, 70, 66, 80, 92].map((h, i) => <div key={i} className="flex-1 bg-emerald-400 rounded-t-sm" style={{
          height: `${h}%`
        }} data-id="element-2156" />)}
          </div>
        </div>
  }, {
    title: 'Weekly Shipped',
    description: 'What every team actually shipped this week – auto-compiled from Linear, GitHub, and Slack.',
    icon: <Activity size={16} className="text-neutral-400" data-id="element-2157" />,
    connections: 5,
    uiPreview: <div className="flex flex-col gap-2 h-full justify-center" data-id="element-2158">
          {[{
        team: 'Engineering',
        note: '14 PRs merged',
        c: 'bg-indigo-500'
      }, {
        team: 'Sales',
        note: '6 deals closed',
        c: 'bg-blue-500'
      }, {
        team: 'Marketing',
        note: '2 launches live',
        c: 'bg-pink-500'
      }, {
        team: 'Product',
        note: '3 features out',
        c: 'bg-purple-500'
      }].map((r, i) => <div key={i} className="flex items-center justify-between bg-[#FAF8F4] border border-neutral-100 rounded-md px-2.5 py-1.5" data-id="element-2159">
              <div className="flex items-center gap-2" data-id="element-2160">
                <span className={`w-1.5 h-1.5 rounded-full ${r.c}`} data-id="element-2161" />
                <span className="text-[10px] font-medium text-[#333333]" data-id="element-2162">
                  {r.team}
                </span>
              </div>
              <span className="text-[10px] text-neutral-500" data-id="element-2163">{r.note}</span>
            </div>)}
        </div>
  }],
  Product: [{
    title: 'Roadmap Health',
    description: 'Every initiative with live status, owners, and risk – synced from Linear and Jira.',
    icon: <Map size={16} className="text-neutral-400" data-id="element-2164" />,
    connections: 3,
    uiPreview: <div className="flex flex-col gap-2.5 h-full justify-center" data-id="element-2165">
          {[{
        name: 'Billing v2',
        status: 'On track',
        c: 'text-blue-600 bg-blue-50 border-blue-100',
        w: '70%',
        bar: 'bg-blue-500'
      }, {
        name: 'Mobile app',
        status: 'At risk',
        c: 'text-orange-600 bg-orange-50 border-orange-100',
        w: '35%',
        bar: 'bg-orange-500'
      }, {
        name: 'SSO / SAML',
        status: 'Shipped',
        c: 'text-green-700 bg-green-50 border-green-100',
        w: '100%',
        bar: 'bg-green-500'
      }].map((r, i) => <div key={i} className="flex flex-col gap-1" data-id="element-2166">
              <div className="flex items-center justify-between" data-id="element-2167">
                <span className="text-[10px] font-medium text-[#333333]" data-id="element-2168">
                  {r.name}
                </span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded border ${r.c}`} data-id="element-2169">
                  {r.status}
                </span>
              </div>
              <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden" data-id="element-2170">
                <div className={`h-full ${r.bar}`} style={{
            width: r.w
          }} data-id="element-2171" />
              </div>
            </div>)}
        </div>
  }, {
    title: 'Feedback Themes',
    description: 'Top themes from support, reviews, and calls – clustered automatically.',
    icon: <MessageCircle size={16} className="text-neutral-400" data-id="element-2172" />,
    connections: 4,
    uiPreview: <div className="flex flex-col gap-2.5 h-full justify-center" data-id="element-2173">
          {[{
        theme: 'Onboarding friction',
        n: 42,
        w: '100%'
      }, {
        theme: 'Mobile bugs',
        n: 28,
        w: '66%'
      }, {
        theme: 'Pricing confusion',
        n: 17,
        w: '40%'
      }].map((r, i) => <div key={i} className="flex flex-col gap-1" data-id="element-2174">
              <div className="flex items-center justify-between" data-id="element-2175">
                <span className="text-[10px] text-[#333333]" data-id="element-2176">{r.theme}</span>
                <span className="text-[10px] font-bold text-neutral-500" data-id="element-2177">
                  {r.n}
                </span>
              </div>
              <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden" data-id="element-2178">
                <div className="h-full bg-pink-400" style={{
            width: r.w
          }} data-id="element-2179" />
              </div>
            </div>)}
        </div>
  }],
  'Customer Success': [{
    title: 'Churn Radar',
    description: 'At-risk accounts ranked by health score – from product usage, tickets, and CRM.',
    icon: <HeartPulse size={16} className="text-neutral-400" data-id="element-2180" />,
    connections: 4,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-2181">
          <div className="grid grid-cols-3 gap-2 border-b border-neutral-200 pb-2" data-id="element-2182">
            <span className="text-[9px] text-neutral-500 col-span-2" data-id="element-2183">
              ACCOUNT
            </span>
            <span className="text-[9px] text-neutral-500" data-id="element-2184">HEALTH</span>
          </div>
          {[{
        name: 'Initech',
        score: 41,
        c: 'bg-red-500',
        pill: 'text-red-600 bg-red-50 border-red-100',
        risk: 'Critical'
      }, {
        name: 'Acme Corp',
        score: 62,
        c: 'bg-yellow-500',
        pill: 'text-orange-600 bg-orange-50 border-orange-100',
        risk: 'At risk'
      }, {
        name: 'Globex',
        score: 88,
        c: 'bg-green-500',
        pill: 'text-green-700 bg-green-50 border-green-100',
        risk: 'Healthy'
      }].map((r, i) => <div key={i} className="grid grid-cols-3 gap-2 items-center" data-id="element-2185">
              <div className="flex items-center gap-1.5 col-span-2" data-id="element-2186">
                <span className={`w-1.5 h-1.5 rounded-full ${r.c}`} data-id="element-2187" />
                <span className="text-[10px] text-[#333333] font-medium truncate" data-id="element-2188">
                  {r.name}
                </span>
              </div>
              <span className={`text-[9px] px-1.5 py-0.5 rounded border ${r.pill} text-center`} data-id="element-2189">
                {r.score}
              </span>
            </div>)}
        </div>
  }, {
    title: 'Ticket Pulse',
    description: 'Live support volume, CSAT, and SLA – pulled from Intercom and Zendesk.',
    icon: <Ticket size={16} className="text-neutral-400" data-id="element-2190" />,
    connections: 3,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-2191">
          <div className="flex justify-between items-end mb-1" data-id="element-2192">
            <div className="flex flex-col" data-id="element-2193">
              <span className="text-[10px] text-neutral-500" data-id="element-2194">Open</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-2195">37</span>
            </div>
            <div className="flex flex-col text-center" data-id="element-2196">
              <span className="text-[10px] text-neutral-500" data-id="element-2197">CSAT</span>
              <span className="text-sm font-bold text-green-600" data-id="element-2198">94%</span>
            </div>
            <div className="flex flex-col text-right" data-id="element-2199">
              <span className="text-[10px] text-neutral-500" data-id="element-2200">SLA</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-2201">98%</span>
            </div>
          </div>
          <div className="flex items-end gap-1.5 h-12 mt-auto border-b border-neutral-200 pb-1" data-id="element-2202">
            {[60, 45, 70, 50, 80, 40, 55].map((h, i) => <div key={i} className="flex-1 bg-blue-400 rounded-t-sm" style={{
          height: `${h}%`
        }} data-id="element-2203" />)}
          </div>
        </div>
  }]
};
const personas: Persona[] = ['Leadership', 'Sales', 'Marketing', 'Product', 'Engineering', 'Customer Success', 'Operations'];
const personaPrompt: Record<Persona, string> = {
  Leadership: 'build me a live business pulse and a weekly cross-team shipped report',
  Sales: 'build me a live pipeline tracker and a deal war room from our CRM',
  Marketing: 'build me a campaign ROI dashboard across Meta, Google, and LinkedIn',
  Product: 'build me a roadmap health board and cluster our user feedback themes',
  Engineering: 'build me an incident command center from Sentry and Datadog',
  'Customer Success': 'build me a churn radar and a live support ticket dashboard',
  Operations: 'build me a vendor spend tracker and headcount plan from Xero and Greenhouse'
};
export function ProductDemo() {
  const copy = useCopy();
  const [activePersona, setActivePersona] = useState<Persona>('Leadership');
  return <section className="bg-[#FAF8F4] py-12 md:py-32 border-t border-neutral-200 relative overflow-hidden" data-id="element-2204">
      {/* Halftone dot-matrix texture — matches the other cream sections */}
      <div aria-hidden="true" className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-2205" />
      {/* Subtle background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#F97316]/10 blur-[120px] rounded-full pointer-events-none z-0" data-id="element-2206" />

      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10" data-id="element-2207">
        <div className="text-left md:text-center mb-12 md:mb-16" data-id="element-2208">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-2209">
            Your command center
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight leading-[1.1] text-[#2B211A] mb-5 md:mb-6" data-id="element-2210">
            {copy.productDemo.headline}
          </h2>
          <p className="text-base md:text-xl text-neutral-500 leading-relaxed max-w-2xl mx-auto mb-8 md:mb-10" data-id="element-2211">
            Bring workflows, automation, and context into one place.
          </p>

          {/* Persona Tabs */}
          <div className="flex overflow-x-auto md:flex-wrap justify-start md:justify-center gap-2 md:gap-4 pb-2 md:pb-0 -mx-6 px-6 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-2212">
            {personas.map(persona => {
            const isActive = activePersona === persona;
            return <button key={persona} onClick={() => setActivePersona(persona)} className={`shrink-0 whitespace-nowrap px-4 py-2 md:px-5 md:py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${isActive ? 'bg-white text-[#433832] shadow-sm border border-[#EAE4D9]' : 'text-neutral-500 hover:text-[#433832] hover:bg-white/60 border border-transparent'}`} data-id="element-2213">
                  {persona}
                </button>;
          })}
          </div>
        </div>

        {/* Product Window Shell */}
        <div className="w-full rounded-[1.5rem] border border-[#EAE4D9] bg-white shadow-2xl shadow-orange-900/5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-2214">
          <div className="flex flex-row min-w-[900px] min-h-[500px]" data-id="element-2215">
            {/* Left Sidebar */}
            <div className="w-56 bg-[#FAF8F4] border-r border-[#EAE4D9] p-4 flex flex-col gap-6 shrink-0" data-id="element-2216">
              <div className="flex items-center justify-between px-2" data-id="element-2217">
                <div className="flex items-center gap-2" data-id="element-2218">
                  <div className="w-5 h-5 rounded bg-[#F97316] flex items-center justify-center" data-id="element-2219">
                    <span className="text-[#433832] font-bold text-[10px]" data-id="element-2220">
                      A
                    </span>
                  </div>
                  <span className="text-[#433832] font-bold text-sm" data-id="element-2221">Acme</span>
                </div>
                <AnimatePresence mode="wait" data-id="element-2222">
                  <motion.span key={activePersona} initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.9
                }} transition={{
                  duration: 0.2
                }} className="text-[9px] font-bold uppercase tracking-wider bg-white border border-[#EAE4D9] text-neutral-500 px-1.5 py-0.5 rounded-md" data-id="element-2223">
                    {activePersona}
                  </motion.span>
                </AnimatePresence>
              </div>

              <nav className="flex flex-col gap-1" data-id="element-2224">
                <div className="px-2 py-1.5 text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1" data-id="element-2225">
                  Workspace
                </div>
                <button className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-neutral-600 hover:text-[#433832] hover:bg-orange-50/50 transition-colors text-sm font-medium" data-id="element-2226">
                  <LayoutGrid size={16} className="text-neutral-400" data-id="element-2227" />
                  Home
                </button>
                <button className="flex items-center gap-3 px-2 py-2.5 rounded-lg bg-white text-[#433832] border border-[#EAE4D9] shadow-sm transition-colors text-sm font-medium w-full text-left" data-id="element-2228">
                  <LayoutGrid size={16} className="shrink-0 text-[#F97316]" data-id="element-2229" />
                  <span className="flex-1" data-id="element-2230">Dashboards</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded-full shrink-0" data-id="element-2231">
                    Soon
                  </span>
                </button>
                <button className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-neutral-600 hover:text-[#433832] hover:bg-orange-50/50 transition-colors text-sm font-medium w-full text-left" data-id="element-2232">
                  <MessageSquare size={16} className="shrink-0 text-neutral-400" data-id="element-2233" />
                  <span className="flex-1" data-id="element-2234">Chat</span>
                </button>
                <button className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-neutral-600 hover:text-[#433832] hover:bg-orange-50/50 transition-colors text-sm font-medium w-full text-left" data-id="element-2235">
                  <FolderOpen size={16} className="shrink-0 text-neutral-400" data-id="element-2236" />
                  <span className="flex-1" data-id="element-2237">Files</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded-full shrink-0" data-id="element-2238">
                    Soon
                  </span>
                </button>
                <button className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-neutral-600 hover:text-[#433832] hover:bg-orange-50/50 transition-colors text-sm font-medium w-full text-left" data-id="element-2239">
                  <Plug size={16} className="shrink-0 text-neutral-400" data-id="element-2240" />
                  <span className="flex-1" data-id="element-2241">Integrations</span>
                </button>
              </nav>

              {/* Contextual Persona Nav */}
              <nav className="flex flex-col gap-1" data-id="element-2242">
                <AnimatePresence mode="wait" data-id="element-2243">
                  <motion.div key={activePersona} initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -4
                }} transition={{
                  duration: 0.25
                }} className="flex flex-col gap-1" data-id="element-2244">
                    <div className="px-2 py-1.5 text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1" data-id="element-2245">
                      {activePersona}
                    </div>
                    {personaData[activePersona].map((app, idx) => <button key={idx} className={`flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium w-full text-left transition-colors ${idx === 0 ? 'bg-orange-50/80 text-[#433832] border border-orange-100/50 shadow-sm' : 'text-neutral-500 hover:text-[#433832] hover:bg-orange-50/50 border border-transparent'}`} data-id="element-2246">
                        <div className={`shrink-0 scale-90 ${idx === 0 ? 'opacity-100' : 'opacity-70'}`} data-id="element-2247">
                          {app.icon}
                        </div>
                        <span className="flex-1 truncate" data-id="element-2248">{app.title}</span>
                      </button>)}
                  </motion.div>
                </AnimatePresence>
              </nav>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 p-6 md:p-8 bg-[#FFFCF8]" data-id="element-2249">
              <div className="flex flex-row justify-between items-center gap-4 mb-6" data-id="element-2250">
                <div className="flex flex-wrap items-center gap-2" data-id="element-2251">
                  <h3 className="text-xl font-bold text-[#433832]" data-id="element-2252">
                    Dashboards
                  </h3>
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFEDD5] text-[#EA580C] px-2 py-0.5 rounded-full ml-1" data-id="element-2253">
                    Preview · Coming soon
                  </span>
                  <span className="inline text-neutral-400 ml-1" data-id="element-2254">/</span>
                  <button className="flex items-center gap-1 text-[#433832] font-medium hover:text-[#F97316] transition-colors" data-id="element-2255">
                    {activePersona} <ChevronDown size={16} data-id="element-2256" />
                  </button>
                </div>
                <div className="flex gap-3 w-auto" data-id="element-2257">
                  <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#EAE4D9] text-[#433832] text-sm font-medium hover:bg-orange-50/50 transition-colors" data-id="element-2258">
                    <Upload size={14} className="text-neutral-400" data-id="element-2259" />
                    Create from files
                  </button>
                  <button className="flex flex-none items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#F97316] text-white text-sm font-medium hover:bg-[#EA580C] transition-colors shadow-sm shadow-orange-500/20" data-id="element-2260">
                    <Plus size={14} data-id="element-2261" />
                    Create new
                  </button>
                </div>
              </div>

              {/* @Giga prompt that produced these apps — bridges Slack → workspace */}
              <div className="mb-6 rounded-2xl bg-[#FAF8F4] border border-[#EAE4D9] p-5 shadow-sm" data-id="element-2262">
                <div className="flex items-start gap-3" data-id="element-2263">
                  <div className="w-8 h-8 rounded-xl bg-[#3F0E40] flex items-center justify-center shrink-0 shadow-sm" data-id="element-2264">
                    <MessageSquare size={16} className="text-white" data-id="element-2265" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-2266">
                    <span className="text-[11px] text-neutral-500 block mb-1 font-medium" data-id="element-2267">
                      Magic from Slack
                    </span>
                    <AnimatePresence mode="wait" data-id="element-2268">
                      <motion.p key={activePersona} initial={{
                      opacity: 0,
                      y: 4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -4
                    }} transition={{
                      duration: 0.25
                    }} className="text-[15px] text-[#433832] leading-snug" data-id="element-2269">
                        <span className="text-[#1264A3] font-medium" data-id="element-2270">
                          @Giga
                        </span>{' '}
                        {personaPrompt[activePersona]}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 self-center rounded-full bg-white border border-[#EAE4D9] px-3 py-1.5 shadow-sm" data-id="element-2271">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C]" data-id="element-2272" />
                    <span className="text-[11px] font-bold text-[#EA580C]" data-id="element-2273">
                      Giga built {personaData[activePersona].length} dashboards
                    </span>
                  </div>
                </div>
              </div>

              <AnimatePresence mode="wait" data-id="element-2274">
                <motion.div key={activePersona} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.3
              }} className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2275">
                  {personaData[activePersona].map((app, idx) => <div key={idx} className="group bg-white border border-[#EAE4D9] rounded-2xl overflow-hidden hover:border-orange-200 transition-all duration-300 flex flex-col shadow-sm hover:shadow-md hover:-translate-y-0.5" data-id="element-2276">
                      {/* App UI Mockup Area */}
                      <div className="h-48 bg-[#FAF8F4] border-b border-[#EAE4D9] p-4 relative overflow-hidden" data-id="element-2277">
                        {/* Top browser-like bar */}
                        <div className="flex items-center gap-2 mb-4" data-id="element-2278">
                          {app.icon}
                          <span className="text-xs font-bold text-[#433832]" data-id="element-2279">
                            {app.title}
                          </span>
                        </div>
                        {/* The actual UI preview */}
                        <div className="absolute top-12 left-4 right-4 bottom-0 bg-white border border-[#EAE4D9] border-b-0 rounded-t-xl p-4 shadow-sm" data-id="element-2280">
                          {app.uiPreview}
                        </div>
                      </div>

                      {/* App Meta */}
                      <div className="p-5 flex flex-col flex-1" data-id="element-2281">
                        <h4 className="text-base font-bold text-[#433832] mb-2" data-id="element-2282">
                          {app.title}
                        </h4>
                        <p className="text-sm text-neutral-600 mb-6 flex-1" data-id="element-2283">
                          {app.description}
                        </p>

                        <div className="flex items-center justify-between mt-auto pt-4 border-t border-neutral-100" data-id="element-2284">
                          <span className="text-xs text-neutral-500 font-medium" data-id="element-2285">
                            {app.connections} Connections · Active
                          </span>
                          {/* Slack Tie-in */}
                          <div className="flex items-center gap-1.5 bg-[#1264A3]/5 px-2 py-1 rounded text-[10px] font-medium text-[#1264A3] border border-[#1264A3]/10" data-id="element-2286">
                            <span data-id="element-2287">@Giga</span>
                            <span className="text-[#1264A3]/70 font-normal" data-id="element-2288">
                              synced to Slack
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}